import {
    ChangeDetectionStrategy,
    Component,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
    ViewChild,
    AfterViewInit
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
    DataService,
    DynamicFormInputComponent,
    FormInputComponent,
    SharedModule,
    StringCustomFieldConfig
} from '@vendure/admin-ui/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    getProductCustomizationOptionDocument,
    GET_PRODUCT_CUSTOMIZATION_OPTION_IDS_BY_PRODUCT,
} from '../common/graphql';

@Component({
    selector: 'app-ordered-customization-options-input',
    template: `
        <div *ngIf="warningMessage" class="alert alert-warning alert-sm" role="alert">
            <div class="alert-items">
                <div class="alert-item static">
                    <div class="alert-icon-wrapper">
                        <clr-icon class="alert-icon" shape="exclamation-triangle"></clr-icon>
                    </div>
                    <span class="alert-text">{{ warningMessage }}</span>
                </div>
            </div>
        </div>
        <vdr-dynamic-form-input #orderedCustomizationInput
            [control]="optionsControl"
            [def]="{
                type: 'string',
                list: true,
                ui: {
                    component: 'product-customization-option-selector'
                }
            }"
        >
        </vdr-dynamic-form-input>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SharedModule],
})
export class OrderedCustomizationOptionsInputComponent implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy {
    @ViewChild('orderedCustomizationInput') orderedCustomizationInput: DynamicFormInputComponent;
    readonly: boolean;
    config: StringCustomFieldConfig;
    formControl: FormControl;

    optionsControl = new UntypedFormControl();
    optionIds: string[] = [];
    warningMessage: string | null = null;

    private destroy$ = new Subject<void>();
    private isVariant = false;
    private productOptionIds: string[] | null = null;

    constructor(
        private cdr: ChangeDetectorRef,
        private dataService: DataService,
        private route: ActivatedRoute,
    ) {}

    ngOnInit() {
        this.optionIds = this.formControl.value ? JSON.parse(this.formControl.value) : [];
        this.isVariant = this.detectVariantFromLabel();

        if (this.isVariant) {
            this.fetchProductOptionsAndCheck();
        } else {
            this.checkOptionCompatibility();
        }
    }

    ngAfterViewInit() {
        if(this.orderedCustomizationInput) {
            this.orderedCustomizationInput.writeValue(this.optionIds);
            this.orderedCustomizationInput.registerOnChange(this.onChange);
        }
        this.optionsControl.setValue(this.optionIds, { emitEvent: false });
        this.cdr.markForCheck();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onChange = (value: any) => {
        if(this.optionIds != value) {
            this.optionIds = value;
            const jsonString = JSON.stringify(value);
            this.formControl.setValue(jsonString);
            this.formControl.markAsDirty();
            this.checkOptionCompatibility();
        }
    }

    private detectVariantFromLabel(): boolean {
        const labels: any[] | undefined = (this.config as any)?.label;
        if (Array.isArray(labels)) {
            return labels.some(
                (l: any) => typeof l?.value === 'string' && l.value.toLowerCase().includes('variant'),
            );
        }
        return false;
    }

    private getProductIdFromRoute(): string | null {
        const segments = this.route?.snapshot?.pathFromRoot;
        if (Array.isArray(segments)) {
            for (const seg of segments) {
                const productId = seg.params?.['productId'];
                if (productId) {
                    return productId;
                }
            }
            for (const seg of segments) {
                const id = seg.params?.['id'];
                if (id) {
                    return id;
                }
            }
        }
        return this.route?.snapshot?.params?.['id']
            ?? this.route?.parent?.snapshot?.params?.['id']
            ?? null;
    }

    private fetchProductOptionsAndCheck() {
        const productId = this.getProductIdFromRoute();
        if (!productId) {
            this.productOptionIds = [];
            this.checkOptionCompatibility();
            return;
        }
        this.dataService
            .query(GET_PRODUCT_CUSTOMIZATION_OPTION_IDS_BY_PRODUCT, { id: productId })
            .mapSingle((data: any) => {
                const raw = data.product?.customFields?.customizationOptions;
                if (!raw) return [];
                try { return JSON.parse(raw); } catch { return []; }
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe(ids => {
                this.productOptionIds = ids;
                this.checkOptionCompatibility();
            });
    }

    private checkOptionCompatibility() {
        const currentIds = this.optionIds?.filter(id => !!id) ?? [];
        if (currentIds.length === 0) {
            this.warningMessage = null;
            this.cdr.markForCheck();
            return;
        }

        const inheritedIds = this.isVariant
            ? (this.productOptionIds ?? []).filter(id => !currentIds.includes(id))
            : [];
        const allIds = [...currentIds, ...inheritedIds];

        forkJoin(
            allIds.map(id =>
                this.dataService
                    .query(getProductCustomizationOptionDocument, { id })
                    .mapSingle((data: any) => data.productCustomizationOption)
            )
        ).pipe(takeUntil(this.destroy$)).subscribe(options => {
            const currentIdSet = new Set(currentIds);
            const currentTypes: string[] = [];
            const allTypes: string[] = [];
            options.forEach((o: any, i: number) => {
                if (!o) return;
                allTypes.push(o.type);
                if (currentIdSet.has(allIds[i])) {
                    currentTypes.push(o.type);
                }
            });
            const hasTextFramesHere = currentTypes.includes('custom-text-frames');
            const hasPrintTemplateAnywhere = allTypes.includes('custom-print-template');
            if (hasTextFramesHere && !hasPrintTemplateAnywhere) {
                if (this.isVariant) {
                    this.warningMessage =
                        'Text Overlay options require a Print Template to function. ' +
                        'No Print Template found at either this variant or the product level — ' +
                        'text overlay data will be ignored at checkout.';
                } else {
                    this.warningMessage =
                        'Text Overlay options require a Print Template to function. ' +
                        'Add a Print Template here, or ensure each variant that uses text overlays has its own Print Template.';
                }
            } else {
                this.warningMessage = null;
            }
            this.cdr.markForCheck();
        });
    }

}
